import type { ComparisonPlanFeature, ComparisonPlanKey } from '../models/billing.types'

export interface ComparisonPlanDefinition {
  key: ComparisonPlanKey
  name: string
  descriptionKey: string
  badgeKey: string | null
  actionKey: string
  featured: boolean
  featureKeys: string[]
  aiFeatureKeys: string[]
}

export const COMPARISON_PLAN_DEFINITIONS: readonly ComparisonPlanDefinition[] = [
  {
    key: 'free',
    name: 'Free',
    descriptionKey: 'credits.comparePlans.plan.free.description',
    badgeKey: null,
    actionKey: 'credits.comparePlans.action.free',
    featured: false,
    featureKeys: [
      'credits.comparePlans.features.free.workspace',
      'credits.comparePlans.features.free.platforms',
      'credits.comparePlans.features.free.monitoring',
      'credits.comparePlans.features.free.budget',
      'credits.comparePlans.features.free.dashboard',
      'credits.comparePlans.features.free.history',
      'credits.comparePlans.features.free.export',
      'credits.comparePlans.features.free.updates',
    ],
    aiFeatureKeys: [
      'credits.comparePlans.ai.creative',
      'credits.comparePlans.ai.orpheus',
      'credits.comparePlans.ai.impact',
    ],
  },
  {
    key: 'growth',
    name: 'Growth',
    descriptionKey: 'credits.comparePlans.plan.growth.description',
    badgeKey: null,
    actionKey: 'credits.comparePlans.action.growth',
    featured: false,
    featureKeys: [
      'credits.comparePlans.features.growth.platforms',
      'credits.comparePlans.features.growth.attribution',
      'credits.comparePlans.features.growth.history',
      'credits.comparePlans.features.growth.dashboards',
      'credits.comparePlans.features.growth.alerts',
      'credits.comparePlans.features.growth.kpi',
      'credits.comparePlans.features.growth.reporting',
      'credits.comparePlans.features.growth.refresh',
    ],
    aiFeatureKeys: [
      'credits.comparePlans.ai.orpheusDescription',
      'credits.comparePlans.ai.creativeDescription',
      'credits.comparePlans.ai.impactDescription',
      'credits.comparePlans.ai.workflowsDescription',
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    descriptionKey: 'credits.comparePlans.plan.pro.description',
    badgeKey: 'credits.comparePlans.badge.mostChosen',
    actionKey: 'credits.comparePlans.action.pro',
    featured: true,
    featureKeys: [
      'credits.comparePlans.features.pro.workspaces',
      'credits.comparePlans.features.pro.platforms',
      'credits.comparePlans.features.pro.bigQuery',
      'credits.comparePlans.features.pro.dashboards',
      'credits.comparePlans.features.pro.funnels',
      'credits.comparePlans.features.pro.attribution',
      'credits.comparePlans.features.pro.support',
    ],
    aiFeatureKeys: [],
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    descriptionKey: 'credits.comparePlans.plan.enterprise.description',
    badgeKey: null,
    actionKey: 'credits.comparePlans.action.enterprise',
    featured: false,
    featureKeys: [
      'credits.comparePlans.features.enterprise.workspaces',
      'credits.comparePlans.features.enterprise.platforms',
      'credits.comparePlans.features.enterprise.bigQuery',
      'credits.comparePlans.features.enterprise.dashboards',
      'credits.comparePlans.features.enterprise.funnels',
      'credits.comparePlans.features.enterprise.sla',
    ],
    aiFeatureKeys: ['credits.comparePlans.ai.enterpriseAllowance'],
  },
] as const

function feature(key: string, labelKey: string, included = true): ComparisonPlanFeature {
  return { key, label: null, labelKey, included }
}

export const COMPARISON_PLAN_LIMITS: Record<ComparisonPlanKey, readonly ComparisonPlanFeature[]> = {
  free: [
    feature('users', 'credits.comparePlans.limits.free.users'),
    feature('mcp-users', 'credits.comparePlans.limits.free.mcpUsers', false),
  ],
  growth: [
    feature('users', 'credits.comparePlans.limits.growth.users'),
    feature('mcp-users', 'credits.comparePlans.limits.growth.mcpUsers'),
  ],
  pro: [
    feature('users', 'credits.comparePlans.limits.pro.users'),
    feature('mcp-users', 'credits.comparePlans.limits.pro.mcpUsers'),
  ],
  enterprise: [
    feature('users', 'credits.comparePlans.limits.enterprise.users'),
    feature('mcp-users', 'credits.comparePlans.limits.enterprise.mcpUsers'),
  ],
}
